/**
 * Suggested Negatives Panel (Phase C.2)
 *
 * Renders the LLM-filter's rejected keywords as candidate negatives,
 * classified by reason (competitor_brand / wrong_industry / info_intent
 * / low_intent_modifier / other). Operator selects which to add to
 * the tenant's account-wide shared negative-keyword list.
 *
 * Wires into the existing P2 NegativeKeywordManager via a new endpoint
 * — see backend/api/routes/google_ads_keyword_research.py.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Ban,
  Building2,
  Download,
  HelpCircle,
  Loader2,
  RefreshCw,
  Tag,
  TriangleAlert,
} from 'lucide-react';
import { toast } from 'sonner';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  addNegativeKeywords,
  listAppliedNegativeKeywords,
  type NegativeKeywordCandidate,
} from '@/services/googleAdsCampaignsService';
import { useTenantStore } from '@/stores/useTenantStore';


type ReasonFilter = 'all' | NegativeKeywordCandidate['reason'];


const REASON_LABELS: Record<string, string> = {
  competitor_brand: 'Competitor brand',
  wrong_industry: 'Wrong industry',
  info_intent: 'Informational',
  low_intent_modifier: 'Low intent',
  non_buyer_intent: 'Non-buyer intent',
  other: 'Other',
};


const REASON_ICONS: Record<string, JSX.Element> = {
  competitor_brand: <Building2 className="h-3 w-3" />,
  wrong_industry: <TriangleAlert className="h-3 w-3" />,
  info_intent: <HelpCircle className="h-3 w-3" />,
  low_intent_modifier: <Tag className="h-3 w-3" />,
  non_buyer_intent: <Tag className="h-3 w-3" />,
  other: <Tag className="h-3 w-3" />,
};


const REASON_BADGE_STYLES: Record<string, string> = {
  competitor_brand: 'bg-destructive/10 text-destructive border-destructive/30',
  wrong_industry: 'bg-coral-light text-coral-dark border-coral/40',
  info_intent: 'bg-teal-light text-teal-dark border-teal/40',
  low_intent_modifier: 'bg-coral-light/60 text-coral-dark border-coral/40',
  non_buyer_intent: 'bg-warm-bg text-foreground border-border',
  other: 'bg-warm-bg text-muted-foreground border-border',
};

const NEGATIVE_KEYWORDS_PAGE_SIZE = 25;
const NEGATIVE_KEYWORD_ACCOUNT_WIDE_CONFIRMATION = 'APPLY ACCOUNT-WIDE NEGATIVES';


interface Props {
  negatives: NegativeKeywordCandidate[];
}


export function SuggestedNegativesPanel({ negatives }: Props) {
  const tenantId = useTenantStore((s) => s.currentTenant ?? '');
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<ReasonFilter>('all');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [manualText, setManualText] = useState('');
  const [phraseMatchOptIn, setPhraseMatchOptIn] = useState(false);
  const [confirmationText, setConfirmationText] = useState('');
  const [page, setPage] = useState(1);
  const appliedQueryKey = ['google-ads', 'applied-negatives', tenantId];

  // Count per reason, for the filter chips
  const counts = useMemo(() => {
    const out: Record<string, number> = {};
    for (const n of negatives) {
      out[n.reason] = (out[n.reason] ?? 0) + 1;
    }
    return out;
  }, [negatives]);

  const visible = useMemo(
    () => (filter === 'all' ? negatives : negatives.filter((n) => n.reason === filter)),
    [negatives, filter],
  );
  const pageCount = Math.max(
    1,
    Math.ceil(visible.length / NEGATIVE_KEYWORDS_PAGE_SIZE),
  );
  const currentPage = Math.min(page, pageCount);
  const paginatedVisible = useMemo(
    () =>
      visible.slice(
        (currentPage - 1) * NEGATIVE_KEYWORDS_PAGE_SIZE,
        currentPage * NEGATIVE_KEYWORDS_PAGE_SIZE,
      ),
    [currentPage, visible],
  );
  const filteredKeywords = useMemo(
    () => visible.map((n) => n.keyword),
    [visible],
  );
  const pageKeywords = useMemo(
    () => paginatedVisible.map((n) => n.keyword),
    [paginatedVisible],
  );
  const selectedVisibleCount = pageKeywords.filter((keyword) =>
    selected.has(keyword),
  ).length;
  const allVisibleSelected =
    pageKeywords.length > 0 && selectedVisibleCount === pageKeywords.length;
  const someVisibleSelected = selectedVisibleCount > 0 && !allVisibleSelected;
  const otherCount = counts.other ?? 0;
  const competitorCount = counts.competitor_brand ?? 0;
  const manualKeywords = useMemo(
    () => parseManualNegativeKeywords(manualText),
    [manualText],
  );
  const keywordsToAdd = useMemo(
    () => dedupeKeywords([...Array.from(selected), ...manualKeywords]),
    [manualKeywords, selected],
  );
  const matchType = phraseMatchOptIn ? 'PHRASE' : 'EXACT';
  const confirmationComplete =
    confirmationText.trim() === NEGATIVE_KEYWORD_ACCOUNT_WIDE_CONFIRMATION;
  const canApplyNegatives =
    keywordsToAdd.length > 0 && confirmationComplete;
  useEffect(() => {
    setPage(1);
  }, [filter, negatives.length]);

  const appliedQuery = useQuery({
    queryKey: appliedQueryKey,
    queryFn: () => listAppliedNegativeKeywords(tenantId),
    enabled: Boolean(tenantId),
    staleTime: 30_000,
  });

  const addMutation = useMutation({
    mutationFn: () =>
      addNegativeKeywords({
        tenant_id: tenantId,
        keywords: keywordsToAdd,
        match_type: matchType,
        phrase_match_opt_in: phraseMatchOptIn,
        confirmation_text: confirmationText.trim(),
      }),
    onSuccess: (res) => {
      toast.success(
        `Added ${res.added} negative${res.added !== 1 ? 's' : ''} to the account-wide list` +
          (typeof res.attached_campaigns === 'number'
            ? ` and attached the list to ${res.attached_campaigns} active campaign${res.attached_campaigns !== 1 ? 's' : ''}`
            : '') +
          (res.skipped ? ` (${res.skipped} already present)` : ''),
      );
      setSelected(new Set());
      setManualText('');
      setConfirmationText('');
      queryClient.invalidateQueries({ queryKey: appliedQueryKey });
    },
    onError: (err: unknown) => {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? String(err);
      toast.error(`Couldn't add negatives: ${detail}`);
    },
  });

  function toggle(keyword: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(keyword)) next.delete(keyword);
      else next.add(keyword);
      return next;
    });
  }

  function selectAllVisible() {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const keyword of filteredKeywords) next.add(keyword);
      return next;
    });
  }

  function selectCurrentPage() {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const keyword of pageKeywords) next.add(keyword);
      return next;
    });
  }

  function toggleAllVisible(checked: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const keyword of pageKeywords) {
        if (checked) next.add(keyword);
        else next.delete(keyword);
      }
      return next;
    });
  }

  function clearSelection() {
    setSelected(new Set());
  }

  function exportCsv() {
    const rows = [
      ['keyword', 'reason', 'reason_detail', 'volume', 'cpc'],
      ...visible.map((n) => [
        n.keyword,
        n.reason,
        n.reason_detail ?? '',
        n.volume?.toString() ?? '',
        n.cpc?.toFixed(2) ?? '',
      ]),
    ];
    const csv = rows
      .map((r) =>
        r
          .map((c) => (/[",\n]/.test(c) ? `"${c.replace(/"/g, '""')}"` : c))
          .join(','),
      )
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `suggested-negatives-${tenantId || 'tenant'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-4">
      <AppliedNegativesPanel
        data={appliedQuery.data}
        isLoading={appliedQuery.isLoading}
        error={appliedQuery.error}
        onRefresh={() => appliedQuery.refetch()}
        isRefreshing={appliedQuery.isFetching}
      />

      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <div className="text-sm font-medium">Manual negatives</div>
              <p className="text-xs text-muted-foreground">
                Add competitor names, competitor + website terms, or obvious non-buyer
                exclusions that were not suggested automatically.
              </p>
            </div>
            <Badge variant="outline" className="text-[10px]">
              {manualKeywords.length} manual
            </Badge>
          </div>
          <Textarea
            rows={3}
            value={manualText}
            onChange={(event) => setManualText(event.target.value)}
            placeholder={'Competitor SEO company\nCompetitor SEO company website\nfree marketing course'}
          />
          <div className="flex flex-wrap items-center gap-2 rounded border border-border bg-warm-bg p-2 text-xs">
            <span className="text-muted-foreground">
              {selected.size} selected · {manualKeywords.length} manual · {selectedVisibleCount}/{pageKeywords.length} on page · {visible.length} filtered
            </span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={selectCurrentPage}
              disabled={pageKeywords.length === 0}
            >
              Select page {pageKeywords.length}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={selectAllVisible}
              disabled={visible.length === 0}
            >
              Select all filtered {visible.length}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={clearSelection}
              disabled={selected.size === 0}
            >
              Clear
            </Button>
            <div className="ml-auto flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                onClick={exportCsv}
              >
                <Download className="mr-1 h-3 w-3" />
                Export CSV
              </Button>
              <Button
                type="button"
                size="sm"
                className="h-7 bg-destructive text-xs text-white hover:bg-destructive/90"
                disabled={!canApplyNegatives || addMutation.isPending}
                onClick={() => addMutation.mutate()}
              >
                {addMutation.isPending ? (
                  <>
                    <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                    Adding…
                  </>
                ) : (
                  <>Add {keywordsToAdd.length} to negative list</>
                )}
              </Button>
            </div>
          </div>
          <div className="space-y-3 rounded border border-coral/40 bg-coral-light/20 p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="text-sm font-medium text-coral-dark">
                Account-wide apply preview
              </div>
              <Badge variant="outline" className="bg-card text-[10px]">
                {matchType}
              </Badge>
            </div>
            <div className="text-xs text-coral-dark/80">
              {keywordsToAdd.length} negative keyword
              {keywordsToAdd.length === 1 ? '' : 's'} will be added to the shared
              account-wide list and attached to active campaigns.
            </div>
            <label className="flex items-start gap-2 text-xs text-coral-dark/80">
              <Checkbox
                checked={phraseMatchOptIn}
                onCheckedChange={(checked) => setPhraseMatchOptIn(checked === true)}
                className="mt-0.5 bg-card"
              />
              <span>
                Use Phrase match instead of the safer Exact default. Phrase can block
                broader query traffic and should only be used after close-variant and
                phrase-overlap review.
              </span>
            </label>
            <div className="space-y-1">
              <Input
                value={confirmationText}
                onChange={(event) => setConfirmationText(event.target.value)}
                placeholder={NEGATIVE_KEYWORD_ACCOUNT_WIDE_CONFIRMATION}
                className="bg-card"
              />
              <div className="text-xs text-coral-dark/80">
                Type{' '}
                <code className="rounded bg-card px-1 py-0.5 text-[11px]">
                  {NEGATIVE_KEYWORD_ACCOUNT_WIDE_CONFIRMATION}
                </code>{' '}
                before applying account-wide negatives.
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {negatives.length > 0 ? (
        <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Ban className="h-4 w-4 text-destructive" />
          Suggested negative keywords
          <Badge variant="outline" className="text-[10px]">
            {negatives.length}
          </Badge>
        </CardTitle>
        <CardDescription>
          This is a suggested review list, not the full account negative list.
          It combines filtered-out keyword ideas, standard non-buyer exclusions,
          and configured competitor names/domains when available.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {otherCount > 0 && (
          <div className="flex items-start gap-2 rounded border border-coral/40 bg-coral-light/20 p-3 text-sm">
            <TriangleAlert className="mt-0.5 h-4 w-4 flex-shrink-0 text-coral-dark" />
            <div>
              <div className="font-medium text-coral-dark">
                Review uncategorized negatives before adding
              </div>
              <p className="text-xs text-muted-foreground">
                {otherCount} candidate{otherCount !== 1 ? 's' : ''} landed in
                the Other bucket. Add only terms that are clearly irrelevant to
                the campaign.
              </p>
            </div>
          </div>
        )}

        {competitorCount === 0 && (
          <div className="flex items-start gap-2 rounded border border-border bg-warm-bg p-3 text-sm">
            <Building2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground" />
            <div>
              <div className="font-medium">No competitor-brand negatives found</div>
              <p className="text-xs text-muted-foreground">
                Competitor exclusions come from Brand Bible competitors, the workspace blocklist,
                and Workspace Settings competitor domains. Add known SEO competitors,
                rerun research, or type manual competitor negatives below.
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                <a
                  href="/brand-bible"
                  className="text-xs font-medium text-teal hover:underline"
                >
                  Open Brand Bible
                </a>
                <a
                  href="/workspace-settings"
                  className="text-xs font-medium text-teal hover:underline"
                >
                  Open Workspace Settings
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Filter chips */}
        <div className="flex flex-wrap items-center gap-2">
          <FilterChip
            active={filter === 'all'}
            onClick={() => setFilter('all')}
            label={`All (${negatives.length})`}
          />
          {Object.entries(counts).map(([reason, count]) => (
            <FilterChip
              key={reason}
              active={filter === reason}
              onClick={() => setFilter(reason as ReasonFilter)}
              label={`${REASON_LABELS[reason] ?? reason} (${count})`}
              icon={REASON_ICONS[reason]}
            />
          ))}
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-muted-foreground">
              <tr className="border-b border-border/50">
                <th className="w-10 py-1.5 text-left">
                  <Checkbox
                    aria-label={
                      allVisibleSelected
                        ? 'Clear visible negative keywords'
                        : 'Select all visible negative keywords'
                    }
                    checked={allVisibleSelected ? true : someVisibleSelected ? 'indeterminate' : false}
                    disabled={visible.length === 0}
                    onCheckedChange={(checked) => toggleAllVisible(checked === true)}
                  />
                </th>
                <th className="py-1.5 text-left font-medium">Keyword</th>
                <th className="py-1.5 text-left font-medium">Reason</th>
                <th className="py-1.5 text-right font-medium">Volume</th>
                <th className="py-1.5 text-right font-medium">CPC</th>
              </tr>
            </thead>
            <tbody>
              {paginatedVisible.map((n) => (
                <tr key={n.keyword} className="border-b border-border/30 last:border-0">
                  <td className="py-1.5">
                    <Checkbox
                      checked={selected.has(n.keyword)}
                      onCheckedChange={() => toggle(n.keyword)}
                    />
                  </td>
                  <td className="py-1.5">
                    <div>{n.keyword}</div>
                    {n.reason_detail && (
                      <div className="text-[11px] text-muted-foreground">
                        {n.reason_detail}
                      </div>
                    )}
                  </td>
                  <td className="py-1.5">
                    <Badge
                      variant="outline"
                      className={`text-[10px] ${REASON_BADGE_STYLES[n.reason] ?? ''}`}
                    >
                      <span className="mr-1 inline-flex items-center">
                        {REASON_ICONS[n.reason]}
                      </span>
                      {REASON_LABELS[n.reason] ?? n.reason}
                    </Badge>
                  </td>
                  <td className="py-1.5 text-right tabular-nums">
                    {n.volume != null ? n.volume.toLocaleString() : '—'}
                  </td>
                  <td className="py-1.5 text-right tabular-nums">
                    {n.cpc != null ? `$${n.cpc.toFixed(2)}` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {visible.length > NEGATIVE_KEYWORDS_PAGE_SIZE && (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded border border-border bg-warm-bg px-3 py-2 text-xs">
            <span className="text-muted-foreground">
              Page {currentPage} of {pageCount} · showing{' '}
              {(currentPage - 1) * NEGATIVE_KEYWORDS_PAGE_SIZE + 1}-
              {Math.min(currentPage * NEGATIVE_KEYWORDS_PAGE_SIZE, visible.length)} of{' '}
              {visible.length}
            </span>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 bg-card text-xs"
                disabled={currentPage <= 1}
                onClick={() => setPage((value) => Math.max(1, value - 1))}
              >
                Previous
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 bg-card text-xs"
                disabled={currentPage >= pageCount}
                onClick={() => setPage((value) => Math.min(pageCount, value + 1))}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-6 text-sm text-muted-foreground">
            This research run did not produce reviewable negative keyword candidates.
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function AppliedNegativesPanel({
  data,
  isLoading,
  error,
  onRefresh,
  isRefreshing,
}: {
  data?: Awaited<ReturnType<typeof listAppliedNegativeKeywords>>;
  isLoading: boolean;
  error: unknown;
  onRefresh: () => void;
  isRefreshing: boolean;
}) {
  const keywords = data?.keywords ?? [];
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <Ban className="h-4 w-4 text-destructive" />
              Applied negative keywords
              <Badge variant="outline" className="text-[10px]">
                {data?.count ?? 0}
              </Badge>
            </CardTitle>
            <CardDescription>
              {data?.shared_set_name
                ? `Google Ads shared set: ${data.shared_set_name}`
                : 'Google Ads shared set read-back'}
            </CardDescription>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8"
            onClick={onRefresh}
            disabled={isRefreshing}
          >
            <RefreshCw className={`mr-1 h-3 w-3 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading applied negatives…
          </div>
        ) : error ? (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded border border-coral/40 bg-coral-light/20 p-3 text-sm">
            <span className="text-coral-dark">
              Could not read applied negatives from Google Ads.
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 bg-card text-xs"
              onClick={onRefresh}
            >
              Retry
            </Button>
          </div>
        ) : !data?.shared_set_resource_name ? (
          <div className="rounded border border-border bg-warm-bg p-3 text-sm text-muted-foreground">
            No account-wide negative list has been created by the webapp yet.
          </div>
        ) : keywords.length === 0 ? (
          <div className="rounded border border-border bg-warm-bg p-3 text-sm text-muted-foreground">
            This shared list is connected, but no negative keywords are applied yet.
          </div>
        ) : (
          <div className="max-h-72 overflow-auto rounded border border-border">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-card text-xs text-muted-foreground">
                <tr className="border-b border-border">
                  <th className="px-3 py-2 text-left font-medium">Keyword</th>
                  <th className="px-3 py-2 text-right font-medium">Match type</th>
                </tr>
              </thead>
              <tbody>
                {keywords.map((item) => (
                  <tr
                    key={`${item.keyword}-${item.resource_name ?? item.match_type}`}
                    className="border-b border-border/40 last:border-0"
                  >
                    <td className="px-3 py-2">{item.keyword}</td>
                    <td className="px-3 py-2 text-right">
                      <Badge variant="outline" className="text-[10px]">
                        {item.match_type}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function parseManualNegativeKeywords(value: string): string[] {
  return dedupeKeywords(
    value
      .split(/[\n,]/)
      .map((item) => item.trim())
      .filter(Boolean),
  );
}

function dedupeKeywords(keywords: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const keyword of keywords) {
    const cleaned = keyword.replace(/\s+/g, ' ').trim();
    const key = cleaned.toLowerCase();
    if (!cleaned || seen.has(key)) continue;
    seen.add(key);
    out.push(cleaned);
  }
  return out;
}


function FilterChip({
  active,
  onClick,
  label,
  icon,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  icon?: JSX.Element;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs transition-colors ' +
        (active
          ? 'border-coral bg-coral-light text-coral-dark'
          : 'border-border bg-card text-muted-foreground hover:bg-warm-bg')
      }
    >
      {icon}
      {label}
    </button>
  );
}
